import type Papa from 'papaparse';
import type { TApiHttpClientRequestConfig } from '~/misc/resource/types';

enum EInputStatus {
  COMPLETE = 'Complete',
  UPLOADING = 'Uploading',
  FAILED = 'Failed',
}

enum EImportParamKey {
  UPRN = 'uprn',
  ADDRESS = 'address',
  ADDRESS_1 = 'address_1',
  ADDRESS_2 = 'address_2',
  ADDRESS_3 = 'address_3',
  ADDRESS_4 = 'address_4',
  ADDRESS_5 = 'address_5',
  ADDRESS_6 = 'address_6',
  ADDRESS_7 = 'address_7',
  POSTCODE = 'postcode',
  CUSTOMER_REFERENCE = 'customer_reference',
  CRM_LINK = 'crm_link',
  LAST_ACTIVITY = 'last_activity',
  LAST_VALUATION = 'last_valuation',
  LAST_LETTING_VALUATION = 'last_letting_valuation',
  SOURCE = 'source',
  CONTACT_TYPE = 'contact_type',
  CONTACT_REFERENCE = 'contact_reference',
  CONTACT_NAME = 'contact_name',
  CONTACT_EMAIL = 'contact_email',
  CONTACT_MARKETING_CONSENT = 'contact_marketing_consent',
  CONTACT_MOBILE_PHONE = 'contact_mobile_phone',
  CONTACT_WORK_PHONE = 'contact_work_phone',
  CONTACT_HOME_PHONE = 'contact_home_phone',
  CONTACT_CREATED = 'contact_created',
  RECORD_CREATED_DATE = 'created',
}

enum EMappingVariant {
  UPRN = 'uprn',
  SINGLE_ADDRESS = 'single_address',
  MULTI_ADDRESS = 'multi_address',
}

enum ESourceType {
  NONE = 'none',
  FROM_CSV = 'from_csv',
  SINGLE = 'single',
}

type TBaseImportRow = {
  row_num: number;
  row_raw_data: string;
  postcode?: string | null;
  customer_reference?: string | null;
  crm_link?: string | null;
  last_activity?: string | null;
  last_valuation?: string | null;
  last_letting_valuation?: string | null;
  source?: string | null;
  contact_type?: string | null;
  contact_reference?: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_marketing_consent?: string | null;
  contact_mobile_phone?: string | null;
  contact_work_phone?: string | null;
  contact_home_phone?: string | null;
  contact_created?: string | null;
  created?: string | null;
};

type TUprnImportRow = TBaseImportRow & {
  uprn?: number | null;
};

type TAddressImportRow = TBaseImportRow & {
  full_address?: string | null;
};

type TImportColumnIndexes = Record<string, number>;

type TIgnoredColumnIndexes = Record<number, boolean>;

type TImportParams = {
  rows: TBaseImportRow[];
};

type TImportInvalid = {
  row_num: number;
  row_raw_data: string;
  error: string;
};

type TImportResponse = {
  inserted: number;
  updated: number;
  invalid: TImportInvalid[];
};

type TImportStats = {
  added: number;
  updated: number;
  invalid: number;
  failed: number;
};

type TImportParser = Papa.Parser;

type TImportParserStepResult<T> = Papa.ParseStepResult<T>;

type TImportParserResult<T> = Papa.ParseResult<T>;

type TChunk = string[][];

type TImportValidationError = { row_num: number; row_raw_data: string; error: string };

type TImportRequestErrors = Record<string, string[]>;

type TImportFormattedError = {
  title: string;
  items: string[];
};

interface IImportModel {
  process(
    params: TImportParams,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TImportResponse>;
}

interface IImportErrorFormatter {
  process(errors: unknown): TImportFormattedError[];
}

export type {
  TImportParams,
  TImportResponse,
  TBaseImportRow,
  TUprnImportRow,
  TAddressImportRow,
  TImportColumnIndexes,
  TIgnoredColumnIndexes,
  TImportParser,
  TChunk,
  TImportParserStepResult,
  TImportParserResult,
  TImportStats,
  IImportModel,
  TImportValidationError,
  TImportRequestErrors,
  TImportFormattedError,
  IImportErrorFormatter,
};

export { EInputStatus, EImportParamKey, EMappingVariant, ESourceType };
